import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Divider,
  Link,
  Alert,
  Stack,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Link as MuiLink } from '@mui/material';

import axios from "axios";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setRole } from "../Reducers/role";
import { AWS_LINK, DEV_LINK } from "../Static/static";

// Validation Schema
const LoginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email format").required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loginType, setLoginType] = useState("ROOT");
  const [error, setError] = useState({ open: false, title: "", message: "" });

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    const payload = { ...values, type: loginType };
    const LINK = process.env.NODE_ENV === "production" ? AWS_LINK : DEV_LINK;

    try {
      const response = await axios.post(`${LINK}/login`, payload, {
        withCredentials: true,
      });

      if (response.status === 200) {
        dispatch(
          setRole({
            role: loginType,
            id: response.data.user.id,
            permissions: response.data.user.permissions,
            organization_id: response.data.user.organization_id,
            email: response.data.user.email,
            token: response.data.token,
            tutor_locations: response.data.tutor_locations,
            tutor_programs: response.data.tutor_programs,
            first_name: response.data.user.first_name,
            last_name: response.data.user.last_name,
          })
        );

        sessionStorage.setItem("token", response.data.token);
        localStorage.setItem("theme", "light");
        navigate("/dashboard");
      }
    } catch (err) {
      const msg = err.response?.data || "Unable to sign in. Please try again.";
      setError({ open: true, title: "Login Failed", message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const roleLabels = {
    ROOT: "Root / Super Admin",
    ADMIN: "Staff / Admin",
    TUTOR: "Tutor",
  };

  return (
    <Box
  sx={{
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "background.default",
    px: 2,
  }}
>
  <Box
    sx={{
      width: "100%",
      maxWidth: 420,
      p: { xs: 3, sm: 5 },
      boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.08)',
      borderRadius: 2,
      bgcolor: 'background.paper',
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        bgcolor: "primary.main",
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
      },
    }}
  >
    {/* Header */}
    <Box textAlign="center" mb={3}>
      <Typography variant="h5" fontWeight={700} color="primary.main" gutterBottom>
        Welcome Back
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Sign in to continue to your account
      </Typography>
    </Box>

    {/* Role Selector */}
    <Box mb={3}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        align="center"
        mb={1}
      >
        I am logging in as
      </Typography>

      <ToggleButtonGroup
        value={loginType}
        exclusive
        onChange={(_, newType) => newType && setLoginType(newType)}
        fullWidth
        sx={{
          "& .MuiToggleButton-root": {
            py: 1.2,
            fontSize: "0.875rem",
            fontWeight: 500,
            borderRadius: 1.5,
            textTransform: "none",
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.2s ease',
          },
          "& .Mui-selected": {
            bgcolor: "secondary.main",
            color: "primary.contrastText",
            "&:hover": { bgcolor: "primary.dark" },
          },
        }}
      >
        <ToggleButton value="ROOT">Root</ToggleButton>
        <ToggleButton value="ADMIN">Staff</ToggleButton>
        <ToggleButton value="TUTOR">Tutor</ToggleButton>
      </ToggleButtonGroup>

      <Typography
        variant="caption"
        color="text.secondary"
        align="center"
        display="block"
        mt={1}
      >
        Signing in as{' '}
        <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
          {roleLabels[loginType]}
        </Box>
      </Typography>
    </Box>

    <Divider sx={{ mb: 3 }} />

    {/* Error */}
    {error.open && (
      <Alert
        severity="error"
        sx={{ mb: 3, borderRadius: 1.5 }}
        onClose={() => setError({ open: false, title: "", message: "" })}
      >
        <strong>{error.title}</strong> — {error.message}
      </Alert>
    )}

    {/* Form */}
    <Formik
      initialValues={{ email: "", password: "" }}
      validationSchema={LoginSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form noValidate>
          <Stack spacing={2} mb={3}>
            <Field
              as={TextField}
              name="email"
              label="Email address"
              fullWidth
              autoFocus
              size="small"
              autoComplete="email"
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />

            <Field
              as={TextField}
              name="password"
              label="Password"
              type="password"
              fullWidth
              size="small"
              autoComplete="current-password"
              error={touched.password && Boolean(errors.password)}
              helperText={touched.password && errors.password}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Stack>

          {/* Forgot password – inline above button, right-aligned */}
          <Box sx={{ textAlign: 'right', mt: -1, mb: 2 }}>
            <MuiLink
              href="/forgot_password"
              underline="hover"
              variant="caption"
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              Forgot your password?
            </MuiLink>
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              fontWeight: 'bold',
              borderRadius: 1.5,
              textTransform: "none",
              boxShadow: 'none',
              '&:hover': {
                boxShadow: theme => `0 4px 14px ${theme.palette.primary.main + '40'}`
              }
            }}
          >
            {isSubmitting ? "Signing in…" : "Sign In"}
          </Button>
        </Form>
      )}
    </Formik>

    {/* Sign up link */}
    <Box sx={{ textAlign: 'center', mt: 2.5 }}>
      <Typography variant="body2" color="text.secondary">
        Don't have an account?{' '}
        <MuiLink
          href="/register"
          underline="hover"
          fontWeight={600}
          sx={{ color: 'primary.main' }}
        >
          Create one
        </MuiLink>
      </Typography>
    </Box>
  </Box>
</Box>
  );
}